import {
  OTHER_LABEL,
  NONE_LABEL,
  RESERVED_LABELS,
} from 'js/utils/assessment-cohorts';

export const GROUPING_MODE = {
  EACH_ANSWER: 'each_answer',
  CUSTOM: 'custom',
};

export const GROUPING_MODE_OPTIONS = [
  {
    value: GROUPING_MODE.EACH_ANSWER,
    label: 'One cohort per answer',
    description: 'Each answer becomes its own cohort.',
  },
  {
    value: GROUPING_MODE.CUSTOM,
    label: 'Custom cohorts',
    description:
      'Combine answers into named cohorts. Ungrouped answers go to (Other).',
  },
];

export const ANSWER_DRAG_TYPE = 'breakout-answer';

export function groupingModeOption(mode) {
  return (
    GROUPING_MODE_OPTIONS.find((opt) => opt.value === mode) ??
    GROUPING_MODE_OPTIONS[0]
  );
}

export function answerValueKey(answer) {
  return String(answer?.value);
}

export function groupedValueSet(groups = {}) {
  return new Set(
    Object.values(groups).flatMap((values) => (values ?? []).map(String))
  );
}

export function unassignedAnswers(answers = [], groups = {}) {
  const grouped = groupedValueSet(groups);
  return answers.filter((answer) => !grouped.has(answerValueKey(answer)));
}

export function answersInGroup(answers = [], values = []) {
  const wanted = new Set((values ?? []).map(String));
  return answers.filter((answer) => wanted.has(answerValueKey(answer)));
}

export function isReservedGroupLabel(name) {
  const trimmed = String(name ?? '').trim();
  return RESERVED_LABELS.includes(trimmed);
}

export function nextGroupName(groups = {}) {
  const existing = new Set(
    Object.keys(groups).map((name) => name.trim().toLowerCase())
  );
  let n = 1;
  while (existing.has(`cohort ${n}`)) {
    n += 1;
  }
  return `Cohort ${n}`;
}

export function moveAnswerToGroup(groups = {}, answerValue, groupName) {
  const value = String(answerValue);
  const next = {};

  Object.entries(groups).forEach(([name, values]) => {
    next[name] = (values ?? []).map(String).filter((item) => item !== value);
  });

  if (groupName != null) {
    const current = next[groupName] ?? [];
    next[groupName] = [...current, value];
  }

  return next;
}

export function renameGroup(groups = {}, oldName, newName) {
  const trimmed = String(newName ?? '').trim();
  if (!oldName || trimmed === '' || trimmed === oldName) {
    return groups;
  }

  const next = {};
  Object.entries(groups).forEach(([name, values]) => {
    next[name === oldName ? trimmed : name] = values;
  });
  return next;
}

export function removeGroup(groups = {}, groupName) {
  return Object.fromEntries(
    Object.entries(groups).filter(([name]) => name !== groupName)
  );
}

export function compactGroups(groups = {}) {
  return Object.fromEntries(
    Object.entries(groups).filter(
      ([, values]) => Array.isArray(values) && values.length > 0
    )
  );
}

export function canRenameGroup(groups = {}, oldName, newName) {
  const trimmed = String(newName ?? '').trim();
  if (trimmed === '') {
    return { ok: false, reason: 'Name is required' };
  }
  if (isReservedGroupLabel(trimmed)) {
    return {
      ok: false,
      reason: `${NONE_LABEL} and ${OTHER_LABEL} are reserved`,
    };
  }
  const taken = Object.keys(groups).some(
    (name) =>
      name !== oldName && name.trim().toLowerCase() === trimmed.toLowerCase()
  );
  if (taken) {
    return { ok: false, reason: 'A cohort with this name already exists' };
  }
  return { ok: true };
}
